import { Stack } from "@mui/material";
import Cookies from "js-cookie";
import React from "react";
import { useNavigate } from "react-router-dom";
import { logout } from "../../../api/authentication/authentication";
import { getAccessTokenAPI } from "../../../api/config/getAccessToken";
import { DialogSelected } from "../../../constant/common";
import { environment } from "../../../constant/environment";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../../constant/google-analytics/GAEvents";
import { Messages } from "../../../constant/messages/toastMessages";
import { StateContext } from "../../../context/globalContext/context";
import { iGlobalContext } from "../../../context/globalContext/interface";
import { toastMessage } from "../../../helpers/toast/toastMessage";
import useCustomErrorHandler from "../../../hooks/useCustomErrorHandler";
import { Logoutresponse } from "../../../interface";
import { consoleLog } from "../../../utils/consoleLog/consoleLog";
import AppButton from "../button/button";
import { logoutClearStorage } from "../../../utils/logoutClearStorage";
import { br } from "../../../styles/design-tokens";

interface iProps {
  closeDrawer: () => void;
  // Optional override for the Login click. Default opens the login modal (via
  // loginCreateAccountState). The maintenance/CORS-error screen passes a hard
  // reload here — that screen returns BEFORE the login <Dialog> is mounted and
  // its IS_CORS_ERROR gate is latched until a full reload, so opening the modal
  // there does nothing; a reload is the only way to reach a working login.
  onLoginClick?: () => void;
}

export default function Login({ closeDrawer, onLoginClick }: iProps): JSX.Element {
  const navigate = useNavigate();
  const state = React.useContext(StateContext);
  const {
    setNav,
    setMenuOpenState,
    setLoginCreateAccountState,
    setSignInClick,
    setStartingValue,
    setDestValue,
    setClimate,
    setPopulation,
    setExperience,
    setAccomodations,
    setFoodPreferences,
    setMonths,
    setSelectedDestFlag,
    setTripForNDays,
    setIsLoggedIn,
    setBudget,
    setScreen,
    setFirstScreen,
    userInfo,
    setUserInfo,
    setAccessJWT,
    setIsValidAccessToken,
    accessJWT,
  }: iGlobalContext = state;
  const { handleCustomError } = useCustomErrorHandler();
  // Sign in
  function handleSignIn() {
    setSignInClick(true);
    setMenuOpenState(false);
    setLoginCreateAccountState({ flag: true, page: DialogSelected.LOGIN });
    closeDrawer();
  }

  function resetAppState() {
    setScreen("");
    setFirstScreen("");
    window.localStorage.removeItem("_zenvoya_JWT");

    const resetUserInfo = {
      id: "",
      name: "",
      email: "",
      phone: "",
      profile_pic: "",
      loggedIn: false,
      role: 0,
    };
    [setUserInfo].forEach((setFunc) => setFunc(resetUserInfo));
    setIsLoggedIn(false);

    setNav("LANDING_PAGE");
    setSelectedDestFlag(false);
    setStartingValue({ value: "", flag: false });
    setDestValue({ value: "", flag: false });

    resetStateValues([
      setClimate,
      setPopulation,
      setExperience,
      setFoodPreferences,
      setAccomodations,
      setMonths,
      setBudget,
      setTripForNDays,
    ]);

    closeDrawer();
  }

  function resetStateValues(setters: React.Dispatch<React.SetStateAction<any[]>>[]) {
    setters.forEach((setter) => setter([]));
  }

  function postLogoutMessage() {
    const iframe = document.querySelector("#app_domain") as HTMLIFrameElement | null;
    const wind = iframe?.contentWindow;
    wind?.postMessage(JSON.stringify({}), "*");

    if (window && window.parent) {
      const message = {
        action: "logout",
        data: {},
      };
      window.parent.postMessage(JSON.stringify(message));
    }
  }

  async function handleLogoutClick() {
    try {
      const nativeElement = document.getElementById("deviceID") as HTMLInputElement | null;
      if (nativeElement?.value) {
        consoleLog(["LOG OUT VALUE", nativeElement?.value]);
        const response: Logoutresponse = await logout(accessJWT, {
          userId: userInfo?.id,
          deviceId: nativeElement?.value ?? "",
        });
        if (response?.statusCode !== 200) {
          handleCustomError(response?.statusCode, "user.logout");
          return null;
        }
      }

      // Remove cookies in this function as well
      Cookies.remove(`${environment}userData`, {
        path: "/",
        domain: ".zenvoya.ai",
      });

      toastMessage.success(Messages["logout.success"].messageTitle, Messages["logout.success"].message);

      // Clear state and storage
      resetAppState();
      logoutClearStorage();

      // Navigate and trigger post-logout actions
      navigate("/");
      triggerGAEvent(GA_LABEL_ENUMS.Logout);
      postLogoutMessage();
      setIsValidAccessToken(false);
      await getAccessToken();
    } catch (error) {
      console.error("Error during logout:", error);
      toastMessage.error(Messages["loggout.failure.message"].messageTitle, Messages["loggout.failure.message"].message);
    }
  }

  async function getAccessToken() {
    let success = false;
    let count = 0;
    while (!success) {
      count++;
      const response = await getAccessTokenAPI();
      if (response?.statusCode === 200 && response?.data) {
        setAccessJWT(response?.data);
        success = true;
      }

      if (count > 4) break;
    }
  }

  return (
    <Stack zIndex={999} bgcolor="transparent" sx={{ borderRadius: br(4) }} spacing={2}>
      {userInfo?.loggedIn === true ? (
        <AppButton onClick={handleLogoutClick} size="sm">
          Log out
        </AppButton>
      ) : (
        <AppButton
          size="sm"
          onClick={() => {
            triggerGAEvent(GA_LABEL_ENUMS.Login_in_click);
            if (onLoginClick) {
              onLoginClick();
              return;
            }
            handleSignIn();
          }}
        >
          Login
        </AppButton>
      )}
    </Stack>
  );
}
